<template>
  <article class="order-card">
    <header v-if="isMetaVisible" class="order-card__header">
      <div class="order-card__field">
        <span class="order-card__label">{{
          t('account.orders.orderNumber')
        }}</span>
        <span class="order-card__value">#{{ order?.id }}</span>
      </div>

      <div class="order-card__field">
        <span class="order-card__label">{{
          t('account.orders.datePlaced')
        }}</span>
        <span class="order-card__value">{{
          getOrderDate(order?.created_at)
        }}</span>
      </div>

      <div class="order-card__field order-card__field--total">
        <span class="order-card__label">{{
          t('checkout.orderSummary.total')
        }}</span>
        <span class="order-card__value">{{
          formatPriceAmount(order?.total?.amount, locale)
        }}</span>
      </div>
    </header>

    <div class="order-card__items">
      <template v-for="item in order?.items?.data" :key="item.id">
        <AccountOrderItem
          v-if="item.orderStatus?.toUpperCase() !== 'CANCELED'"
          :order-item="item"
          :order-id="order.id"
          :has-customer-invoice="order.has_customer_invoice"
          :order-created-at="order.created_at"
          @invoice-success="handleInvoiceSuccess"
        />
      </template>
    </div>
  </article>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'

const { locale } = useI18n()
const { t } = useI18n()

const { order, isMetaVisible = true } = defineProps<{
  order: any
  isMetaVisible?: boolean
}>()

const emit = defineEmits<{
  'invoice-success': [
    data?: { has_customer_invoice: boolean; orderId?: string },
  ]
}>()

const handleInvoiceSuccess = (data?: { has_customer_invoice: boolean }) => {
  // Emit the invoice success with order ID so parent can update the correct order
  if (data?.has_customer_invoice) {
    emit('invoice-success', {
      has_customer_invoice: true,
      orderId: order.id,
    })
  } else {
    emit('invoice-success', data)
  }
}

const getOrderDate = (date: string) => {
  return dayjs(date).format('dd DD.MM.YYYY')
}
</script>

<style lang="postcss" scoped>
.order-card {
  @apply bg-white rounded-xl overflow-hidden mb-4;
  box-shadow: 0 4px 16px 0 rgba(31, 42, 55, 0.06);

  &:last-child {
    @apply mb-0;
  }

  &__header {
    @apply flex justify-between gap-3 bg-teal-50/40 px-4 py-3;
    @apply border-b border-secondary-active;
  }

  &__field {
    @apply flex flex-col gap-0.5;

    &--total {
      @apply text-right md:text-left col-span-2 md:col-span-1;
    }
  }

  &__label {
    @apply text-xs text-gray-500;
  }

  &__value {
    @apply text-sm font-medium text-gray-900;
  }

  &__items {
    @apply divide-y divide-secondary-active;
  }
}
</style>
